import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { deleteStudentAssessment } from '@/data/services/studentAssessmentsServices';
import { OccurrenceRecord } from '@/utils/assessments/klassPedagogicalSheetRecordTypes';
import { StudentAssessmentRecord } from '@/utils/assessments/studentPedagogicalSheetRecordTypes';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';

type UseStudentRecordsMutationsParams = {
  onSuccess: () => Promise<void> | void;
};

export default function useStudentRecordsMutations({
  onSuccess,
}: UseStudentRecordsMutationsParams) {
  const { t: tOccurrence } = useTranslation('translation', {
    keyPrefix: 'occurrenceAssessmentForm',
  });
  const { t: tAssessment } = useTranslation('translation', {
    keyPrefix: 'behaviorAssessmentForm',
  });

  const onError = (error: unknown) => alert.error(getErrorMessage(error));

  const handleSuccess = async (message: string) => {
    alert.success(message);
    await onSuccess();
  };

  const { mutateAsync: deleteOccurrence } = useMutation(
    ({ assessment }: OccurrenceRecord) =>
      deleteStudentAssessment(assessment.id),
    {
      onSuccess: () => handleSuccess(tOccurrence('successDeletedMessage')),
      onError,
    },
  );

  const { mutateAsync: deleteAssessment } = useMutation(
    ({ assessment }: StudentAssessmentRecord) =>
      deleteStudentAssessment(assessment.id),
    {
      onSuccess: () => handleSuccess(tAssessment('successDeletedMessage')),
      onError,
    },
  );

  return { deleteOccurrence, deleteAssessment };
}
